import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, FileText, Edit, BookOpen, AlertCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const MILESTONES = [
  { key: 'questionnaire', label: 'Questionnaire Submitted', icon: FileText },
  { key: 'outline', label: 'Outline Created', icon: Edit },
  { key: 'first_draft', label: 'First Draft', icon: BookOpen },
  { key: 'revision', label: 'Revision Round', icon: Edit },
  { key: 'final', label: 'Final Manuscript', icon: CheckCircle },
];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/ghostwriter/project/${projectId}`);
        setProject(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Project not found');
      } finally { setLoading(false); }
    };
    fetch();
  }, [projectId]);

  if (loading) return <section className="pt-40 text-center"><p className="text-muted-foreground">Loading project...</p></section>;
  if (error) return (
    <section className="pt-40 text-center">
      <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
      <p className="text-red-400 mb-4">{error}</p>
      <Link to="/ghostwriter/dashboard" className="text-primary hover:underline text-sm">Back to Dashboard</Link>
    </section>
  );

  const currentStep = project?.milestones?.length || 0;

  return (
    <>
      <SeoHead title={`Project — AI Ghostwriter Pro`} noindex={true} />
      <section className="pt-28 pb-20 md:pt-36 md:pb-32">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <Link to="/ghostwriter/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-6" data-testid="back-to-dashboard">
            <ArrowLeft className="w-3 h-3" /> Back to Dashboard
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl tracking-tight" data-testid="project-title">
                {project.package_name || 'Project'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {project.billing_cycle} &middot; {project.payment_method || 'stripe'} &middot;
                <span className={project.status === 'paid' ? 'text-green-400' : 'text-amber-400'}> {project.status}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="font-serif text-xl text-primary">${project.amount?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-muted-foreground">{new Date(project.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Milestone Timeline */}
          <div className="mb-10">
            <h2 className="font-serif text-lg mb-6">Project Timeline</h2>
            <div className="relative">
              {MILESTONES.map((m, i) => {
                const milestone = project.milestones?.find(pm => pm.key === m.key);
                const isComplete = !!milestone;
                const isCurrent = i === currentStep;
                return (
                  <div key={m.key} className="flex gap-4 mb-6 last:mb-0" data-testid={`milestone-${m.key}`}>
                    <div className="relative flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isComplete ? 'bg-green-500/20 border-green-500 text-green-400' : isCurrent ? 'bg-primary/20 border-primary text-primary animate-pulse' : 'bg-white/5 border-white/20 text-muted-foreground'}`}>
                        <m.icon className="w-3.5 h-3.5" />
                      </div>
                      {i < MILESTONES.length - 1 && (
                        <div className={`w-0.5 h-8 mt-1 ${isComplete ? 'bg-green-500/30' : 'bg-white/10'}`} />
                      )}
                    </div>
                    <div className="pt-1">
                      <p className={`text-sm font-semibold ${isComplete ? 'text-green-400' : isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                        {m.label}
                      </p>
                      {milestone?.completed_at && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(milestone.completed_at).toLocaleString()}</p>
                      )}
                      {milestone?.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{milestone.notes}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Project Details */}
          {project.questionnaire && (
            <div className="bg-white/5 border border-white/10 rounded-sm p-6">
              <h2 className="font-serif text-lg mb-4">Book Details</h2>
              <div className="grid grid-cols-2 gap-4">
                {project.questionnaire.genre && <Detail label="Genre" value={project.questionnaire.genre} />}
                {project.questionnaire.book_length && <Detail label="Length" value={project.questionnaire.book_length} />}
                {project.questionnaire.target_audience && <Detail label="Audience" value={project.questionnaire.target_audience} />}
                {project.questionnaire.tone && <Detail label="Tone" value={project.questionnaire.tone} />}
              </div>
              {project.questionnaire.synopsis && (
                <div className="mt-4">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Synopsis</p>
                  <p className="text-xs text-foreground">{project.questionnaire.synopsis}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
